import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpDown, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { DockBar, PageHeader, Section } from "@/components/layout/Page";
import { LocationField } from "@/components/map/LocationField";
import { PinMap } from "@/components/map/PinMap";
import { Stepper } from "@/components/Stepper";
import { VehicleCard } from "@/components/VehicleCard";
import { reverseGeocode, useCreateBooking, useFleet, useQuote } from "@/hooks/queries";
import { errorMessage } from "@/lib/api";
import { AIRPORT, BOOKING_PHONE, SERVICE_TYPES, TIME_SLOTS, dateOptions, firstOpenSlot, isSlotPast, toScheduledISO } from "@/lib/booking";
import { money } from "@/lib/format";

function fareDisplay(quote) {
  if (!quote) return { big: "—", small: "Add your route for an estimate" };
  if (quote.pricing === "hourly") return { big: money(quote.fare), small: `${quote.hours} hrs · ${quote.min_hours}-hr minimum` };
  if (quote.pricing === "zone") return { big: money(quote.fare), small: `Flat airport rate · ${quote.zone}` };
  if (quote.pricing === "starting") return { big: `From ${money(quote.fare)}`, small: `Custom quote · ~${quote.miles} mi · rate confirmed by us` };
  return { big: "Custom Quote", small: `~${quote.miles} mi · we'll confirm your rate` };
}

const CustomPackageNote = () => (
  <a href={BOOKING_PHONE.tel} className="mt-4 flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-ivory transition-colors hover:border-gold/60" data-testid="custom-package-call-link">
    <Phone className="h-4 w-4 shrink-0 text-gold" />
    <span>Interested in a custom package? Call <span className="font-semibold text-gold">{BOOKING_PHONE.display}</span></span>
  </a>
);

function initialSchedule(dates) {
  const open = dates.find((d) => firstOpenSlot(d.value) !== null) || dates[1];
  return { date: open.value, hour: firstOpenSlot(open.value) };
}

function ServiceTabs({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-1 rounded-full border border-line bg-surface p-1" data-testid="service-type-tabs">
      {SERVICE_TYPES.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(s.id)}
          data-testid={`service-type-${s.id}`}
          className={`rounded-full px-3 py-2.5 text-sm transition-[background-color,color] duration-200 ${value === s.id ? "bg-gold font-semibold text-ink" : "text-mist hover:text-ivory"}`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

function Schedule({ dates, date, hour, onDate, onHour }) {
  return (
    <div className="space-y-4">
      <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="date-options">
        {dates.map((d) => (
          <button key={d.value} type="button" className="chip" data-active={d.value === date} onClick={() => onDate(d.value)} data-testid={`date-option-${d.value}`}>
            {d.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-mist" data-testid="atlanta-time-note">All pickup times are Atlanta time (ET).</p>
      <div className="grid grid-cols-4 gap-2" data-testid="time-options">
        {TIME_SLOTS.map((s) => (
          <button
            key={s.value}
            type="button"
            className="chip px-2"
            data-active={s.hour === hour}
            disabled={isSlotPast(date, s.hour)}
            onClick={() => onHour(s.hour)}
            data-testid={`time-option-${s.value}`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Book() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const startType = SERVICE_TYPES.some((s) => s.id === params.get("type")) ? params.get("type") : "transfer";
  const { data: fleet = [] } = useFleet();
  const create = useCreateBooking();
  const dates = useMemo(dateOptions, []);

  const [serviceType, setServiceType] = useState(startType);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(startType === "airport" ? AIRPORT : null);
  const [active, setActive] = useState("pickup");
  const [vehicleId, setVehicleId] = useState("luxury-suv");
  const [schedule, setSchedule] = useState(() => initialSchedule(dates));
  const [passengers, setPassengers] = useState(1);
  const [hours, setHours] = useState(2);
  const [isEvent, setIsEvent] = useState(false);
  const [notes, setNotes] = useState("");

  const hourly = serviceType === "hourly";
  const vehicle = fleet.find((v) => v.id === vehicleId);
  const scheduledISO = schedule.hour === null ? null : toScheduledISO(schedule.date, schedule.hour);
  const ready = Boolean(scheduledISO && (hourly || (pickup && dropoff)));
  const { data: quote, error: quoteError } = useQuote(
    ready
      ? { service_type: serviceType, vehicle_id: vehicleId, pickup: hourly ? null : pickup, dropoff: hourly ? null : dropoff, scheduled_at: scheduledISO, hours: hourly ? hours : null, is_event: hourly && isEvent }
      : null,
  );
  const shownQuote = ready && !quoteError && quote && (quote.pricing === "hourly") === hourly ? quote : null;
  const minHours = quote?.min_hours ?? 2;
  const fare = fareDisplay(shownQuote);

  const prevMin = useRef(minHours);
  useEffect(() => {
    if (!hourly) return;
    const prev = prevMin.current;
    setHours((h) => (h < minHours || h === prev ? minHours : h));
    prevMin.current = minHours;
  }, [hourly, minHours]);

  useEffect(() => {
    if (vehicle) setPassengers((p) => Math.min(p, vehicle.capacity));
  }, [vehicle]);

  const changeType = (t) => {
    setServiceType(t);
    if (t === "airport" && !dropoff) setDropoff(AIRPORT);
    if (t === "hourly") setActive("pickup");
  };
  const changeDate = (date) => setSchedule((s) => ({ date, hour: s.hour !== null && !isSlotPast(date, s.hour) ? s.hour : firstOpenSlot(date) }));
  const setters = { pickup: setPickup, dropoff: setDropoff };
  const pick = async (latlng, kind) => {
    setActive(kind);
    setters[kind]({ address: "Locating address…", lat: latlng.lat, lng: latlng.lng });
    setters[kind](await reverseGeocode(latlng.lat, latlng.lng));
  };
  const swap = () => {
    setPickup(dropoff);
    setDropoff(pickup);
  };

  const missing = !pickup ? "Add a pickup location" : !hourly && !dropoff ? "Add a drop-off location" : schedule.hour === null ? "Choose a pickup time" : null;

  const submit = () => {
    if (missing) return toast.error(missing);
    if (quoteError) return toast.error(errorMessage(quoteError));
    create.mutate(
      {
        service_type: serviceType,
        vehicle_id: vehicleId,
        pickup,
        dropoff: hourly ? null : dropoff,
        scheduled_at: scheduledISO,
        passengers,
        hours: hourly ? hours : null,
        is_event: hourly && isEvent,
        notes,
      },
      {
        onSuccess: (b) => {
          toast.success(b.quote_status === "pending" ? "Request received — we'll confirm your rate shortly" : "Your ride is confirmed");
          navigate(`/trips/${b.id}`);
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  return (
    <div className="pb-28" data-testid="booking-page">
      <PageHeader eyebrow="Reserve your chauffeur" title="Book a ride" />
      <ServiceTabs value={serviceType} onChange={changeType} />

      <div className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Section title={hourly ? "Pickup" : "Route"}>
          <div className="relative space-y-2">
            <LocationField kind="pickup" label="Pickup" value={pickup} onChange={setPickup} active={active === "pickup"} onFocus={() => setActive("pickup")} />
            {!hourly && (
              <>
                <button
                  type="button"
                  onClick={swap}
                  data-testid="swap-locations-btn"
                  className="absolute right-14 top-[52px] z-10 flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface text-mist transition-colors hover:text-gold"
                  title="Swap pickup and drop-off"
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                </button>
                <LocationField kind="dropoff" label="Drop-off" value={dropoff} onChange={setDropoff} active={active === "dropoff"} onFocus={() => setActive("dropoff")} />
              </>
            )}
          </div>
          <div className="mt-4">
            <PinMap pickup={pickup} dropoff={dropoff} active={active} onPick={pick} showDropoff={!hourly} />
          </div>
        </Section>

        <div className="space-y-10">
          <Section title="Your vehicle">
            <div className="no-scrollbar -mx-5 flex snap-x gap-3 overflow-x-auto px-5 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:px-0" data-testid="vehicle-list">
              {fleet.map((v) => (
                <VehicleCard key={v.id} vehicle={v} selected={v.id === vehicleId} hourly={hourly} onSelect={setVehicleId} />
              ))}
            </div>
            <CustomPackageNote />
          </Section>

          <Section title="When">
            <Schedule dates={dates} date={schedule.date} hour={schedule.hour} onDate={changeDate} onHour={(hour) => setSchedule((s) => ({ ...s, hour }))} />
          </Section>

          <Section title="Details">
            <div className="grid gap-3 sm:grid-cols-2">
              <Stepper label="Passengers" value={passengers} min={1} max={vehicle?.capacity || 1} onChange={setPassengers} testId="passengers-stepper" />
              {hourly && <Stepper label="Duration" value={hours} min={minHours} max={12} onChange={setHours} suffix="hours" testId="hours-stepper" />}
            </div>
            {hourly && (
              <div className="mt-3 space-y-3">
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-line bg-elevated/60 px-4 py-3 text-sm text-ivory" data-testid="event-checkbox-label">
                  <input type="checkbox" checked={isEvent} onChange={(e) => setIsEvent(e.target.checked)} className="h-4 w-4 accent-[#CC9440]" data-testid="event-checkbox" />
                  This ride is for an event
                </label>
                <p className="text-xs text-mist" data-testid="hourly-minimum-note">
                  2-hour minimum on weekdays before 6 PM. 3-hour minimum for evenings, weekends and events.
                </p>
              </div>
            )}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Notes for your chauffeur — flight number, preferred temperature, a bottle on ice…"
              className="field mt-3 resize-none"
              data-testid="booking-notes-input"
            />
          </Section>
        </div>
      </div>

      <DockBar testId="confirm-booking-bar">
        <div className="glass flex items-center justify-between gap-4 rounded-3xl px-5 py-3.5 shadow-[0_18px_40px_rgba(0,0,0,0.6)]">
          <div className="min-w-0">
            <p className="eyebrow text-[10px]">Estimated fare</p>
            <p className="font-display text-2xl leading-tight text-gold" data-testid="fare-estimate">
              {fare.big}
            </p>
            <p className="truncate text-[11px] text-mist" data-testid="fare-estimate-detail">
              {ready && quoteError ? errorMessage(quoteError) : fare.small}
            </p>
          </div>
          <button type="button" onClick={submit} disabled={create.isPending} className="btn-gold shrink-0 px-5" data-testid="confirm-booking-btn">
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Booking
          </button>
        </div>
      </DockBar>
    </div>
  );
}
